import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'
import Supercluster from 'supercluster'
import { UnifiedRouteRenderer } from '../../utils/unifiedRouteRenderer'
import { RouteRenderer } from '../../utils/routeRenderer'
import { FlickerFreeRouteRenderer } from '../../utils/flickerFreeRouteRenderer'
import { UnifiedRoute } from '../../services/firebase/routes'
import { getRouteCoordinates, getRouteTypeDisplay } from '../../utils/routeDisplay'
import { createSingleRouteMarker } from './markers/createSingleRouteMarker'
import { createClusterMarker } from './markers/createClusterMarker'
import { renderStaticRoutes } from './rendering/renderStaticRoutes'
import { POIRenderer } from './POIRenderer'
import { EmbedConfig } from '../../services/firebase/embeds'
import { POI } from '@cya-routes/shared'

interface RouteMarkersProps {
  map: mapboxgl.Map | null
  publishedRoutes: UnifiedRoute[]
  onRouteMarkerClick?: (route: any) => void
  onRouteSelect: (route: any) => void
  selectedRoute: any | null
  alwaysRenderRoutes?: boolean // New prop for embed mode
  showMarkers?: boolean // New prop to show/hide markers when routes are permanent
  onLazyLoadRoute?: (routeId: string) => Promise<any> // New prop for lazy loading
  disableRouteClustering?: boolean // New prop to disable clustering
  onPhotoLoad?: (routeId: string) => Promise<string[]> // New prop for loading photos
  embedMode?: boolean // New prop to enable photo slider instead of popup card
  embedConfig?: EmbedConfig // New prop to pass embed configuration
  visiblePOICategories?: string[] // New prop for visible POI categories
  onPOISelect?: (poi: POI) => void // New prop for POI selection
}

export function RouteMarkers({ 
  map, 
  publishedRoutes, 
  onRouteMarkerClick, 
  onRouteSelect, 
  selectedRoute,
  alwaysRenderRoutes = false,
  showMarkers = true,
  onLazyLoadRoute,
  disableRouteClustering = false,
  onPhotoLoad,
  embedMode = false,
  embedConfig,
  visiblePOICategories = [],
  onPOISelect
}: RouteMarkersProps) {
  const markersRef = useRef<mapboxgl.Marker[]>([])
  const clusterRef = useRef<Supercluster | null>(null)
  const unifiedRendererRef = useRef<UnifiedRouteRenderer | null>(null)
  const routeRendererRef = useRef<RouteRenderer | null>(null) // Still needed for static route rendering
  const poiRendererRef = useRef<POIRenderer | null>(null) // POI renderer for embed mode
  const [hoveredRouteId, setHoveredRouteId] = useState<string | null>(null)
  const activeHoverRef = useRef<string | null>(null)
  const activeSelectionRef = useRef<string | null>(selectedRoute?.id || null)
  const markersInitializedRef = useRef(false)

  // Initialize unified route renderer - wait for map to be loaded
  useEffect(() => {
    console.log('🗺️ RouteMarkers useEffect - map:', !!map, 'embedMode:', embedMode, 'alwaysRenderRoutes:', alwaysRenderRoutes)
    if (map) {
      const initializeRenderer = () => {
        if (!unifiedRendererRef.current) {
          console.log('🎨 Creating new UnifiedRouteRenderer')
          console.log('  - embedMode:', embedMode)
          console.log('  - alwaysRenderRoutes:', alwaysRenderRoutes)
          console.log('  - map loaded:', map.loaded())
          // Pass embedMode and alwaysRenderRoutes to ensure proper rendering behavior
          unifiedRendererRef.current = new UnifiedRouteRenderer(map, embedMode, alwaysRenderRoutes)
        } else {
          // Update the alwaysRenderRoutes flag if renderer already exists
          console.log('🔄 Updating UnifiedRouteRenderer')
          console.log('  - Previous alwaysRenderRoutes:', unifiedRendererRef.current)
          console.log('  - New alwaysRenderRoutes:', alwaysRenderRoutes)
          unifiedRendererRef.current.setAlwaysRenderRoutes(alwaysRenderRoutes)
        }
      }
      
      if (map.loaded()) {
        initializeRenderer()
      } else {
        map.once('load', initializeRenderer)
      }
    }
    return () => {
      if (unifiedRendererRef.current) {
        console.log('🗑️ Destroying UnifiedRouteRenderer')
        unifiedRendererRef.current.destroy()
        unifiedRendererRef.current = null
      }
    }
  }, [map, embedMode, alwaysRenderRoutes])

  // Handle route selection and clearing
  useEffect(() => {
    if (!map || !unifiedRendererRef.current) return
    
    console.log('RouteMarkers selectedRoute changed:', selectedRoute)
    activeSelectionRef.current = selectedRoute?.id || null
    
    if (!selectedRoute) {
      // Delay clearing to avoid race conditions with hover state
      const timeoutId = setTimeout(() => {
        // Only clear selected route when popup closes, not hovered routes
        // Check if we have a hovered route before clearing
        if (!hoveredRouteId && !activeHoverRef.current) {
          console.log('Clearing selected route - popup closed and no hover')
          unifiedRendererRef.current?.clearSelectedRoute()
        }
      }, 100) // Small delay to let hover state settle
      
      return () => clearTimeout(timeoutId)
    } else {
      // Render the selected route when popup opens
      console.log('Rendering selected route:', selectedRoute)
      
      // Check if we have valid route data
      if (selectedRoute.firebaseRoute || selectedRoute.coordinates?.length > 0) {
        const routeToRender = selectedRoute.firebaseRoute || selectedRoute
        unifiedRendererRef.current.renderSelectedRoute(routeToRender)
      }
    }
    
    // Update all route marker styles
    document.querySelectorAll('.route-marker').forEach((markerElement: any) => {
      if (markerElement.updateStyle) {
        markerElement.updateStyle()
      }
    })
  }, [selectedRoute, hoveredRouteId])

  // Always render routes for embeds when alwaysRenderRoutes is true
  useEffect(() => {
    if (!map) return
    
    // Clean up any static hover panel when effect re-runs (e.g., when showMarkers changes)
    const existingStaticPanel = document.querySelector('[data-static-hover="true"]')
    if (existingStaticPanel) {
      existingStaticPanel.remove()
    }
    // Also clean up global reference
    if ((window as any).__staticHoverPanel) {
      (window as any).__staticHoverPanel = null
    }
    
    if (alwaysRenderRoutes && publishedRoutes && publishedRoutes.length > 0) {
      console.log('🎨 Always rendering routes for embed mode', publishedRoutes.length, 'routes')
      
      // Check if routes have full coordinate data
      const routesWithFullData = publishedRoutes.filter(r => 
        (r.coordinates?.length > 1) || 
        (r.routes?.length > 0 && r.routes.some((seg: any) => seg.coordinates?.length > 1))
      )
      const routesNeedingData = publishedRoutes.filter(r => 
        r.coordinates?.length === 1 && (!r.routes || r.routes.length === 0)
      )
      
      console.log('📊 Route data status:', {
        total: publishedRoutes.length,
        withFullData: routesWithFullData.length,
        needingData: routesNeedingData.length
      })
      
      // If we have onLazyLoadRoute and routes need data, load them first
      if (onLazyLoadRoute && routesNeedingData.length > 0) {
        console.log('🔄 Loading full data for', routesNeedingData.length, 'routes before rendering...')
        
        // Load all routes that need data
        Promise.all(
          routesNeedingData.map(route => onLazyLoadRoute(route.id))
        ).then(() => {
          console.log('✅ Loaded full data, re-triggering render')
          // Data loaded, the component will re-render with full data
        }).catch(err => {
          console.error('❌ Failed to load some route data:', err)
        })
      }
      
      let handleStyleChange: (() => void) | null = null
      
      // Wait for map to be ready before rendering
      const renderWhenReady = () => {
        if (!map || !map.loaded()) {
          // Try again in a moment
          setTimeout(renderWhenReady, 100)
          return
        }
        
        // Ensure we have a route renderer
        let renderer = routeRendererRef.current
        if (!renderer) {
          console.log('🎨 Creating RouteRenderer for always render')
          renderer = new RouteRenderer(map)
          routeRendererRef.current = renderer
        }
        
        // Static route rendering for maximum performance
        const renderRoutesStatic = () => {
          // Safety check - ensure map is still valid
          if (!map || !map.getStyle) {
            console.warn('⚠️ Map is no longer valid, skipping static route rendering')
            return
          }
          
          renderStaticRoutes({
            map,
            publishedRoutes,
            showMarkers,
            onRouteSelect
          })
        }
        
        renderRoutesStatic()
        
        // Set up style change listener to re-render routes after map style changes
        handleStyleChange = () => {
          console.log('🎨 Map style changed, re-rendering static routes...', {
            alwaysRenderRoutes,
            routeCount: publishedRoutes.length,
            mapLoaded: map?.loaded()
          })
          // Wait longer for the style to fully load and be ready
          setTimeout(() => {
            // For Google styles, the map might report as not loaded initially
            // So we'll try to render anyway and handle errors
            if (map) {
              console.log('🎨 Re-rendering', publishedRoutes.length, 'static routes after style change')
              console.log('   Map loaded status:', map.loaded())
              
              // Function to attempt rendering
              const attemptRender = () => {
                try {
                  renderRoutesStatic()
                  console.log('✅ Successfully re-rendered routes after style change')
                } catch (error) {
                  console.error('❌ Failed to re-render routes after style change:', error)
                  // Try again after a longer delay
                  setTimeout(() => {
                    try {
                      renderRoutesStatic()
                      console.log('✅ Successfully re-rendered routes on retry')
                    } catch (retryError) {
                      console.error('❌ Failed to re-render routes on retry:', retryError)
                    }
                  }, 500)
                }
              }
              
              // If map reports as loaded, render immediately
              if (map.loaded()) {
                attemptRender()
              } else {
                // Otherwise wait for idle event
                console.log('🎨 Map not loaded, waiting for idle event...')
                map.once('idle', () => {
                  console.log('🎨 Map idle, attempting render...')
                  attemptRender()
                })
                // Also try after a timeout as fallback
                setTimeout(attemptRender, 500)
              }
            }
          }, 300) // Increased from 100ms to 300ms to ensure style is fully loaded
        }
        
        map.on('style.load', handleStyleChange)
      }
      
      // Start the rendering process
      renderWhenReady()
      
      // Return cleanup function
      return () => {
        if (handleStyleChange && map) {
          map.off('style.load', handleStyleChange)
        }
      }
      
    } else if (!alwaysRenderRoutes) {
      // Clear all rendered routes when switching back to markers mode
      console.log('🧹 Clearing all rendered routes (switching to markers mode)')
      
      // Clear static route layers first
      try {
        const staticLayerId = 'embed-static-routes-layer'
        const staticSourceId = 'embed-static-routes'
        const strokeLayerId = 'embed-static-routes-stroke'
        const unpavedLayerId = 'embed-static-unpaved-layer'
        const unpavedSourceId = 'embed-static-unpaved'
        const hitAreaLayerId = 'embed-static-routes-hit-area'
        
        // Remove layers in reverse order (top to bottom)
        // Remove hit area layer first
        if (map.getLayer(hitAreaLayerId)) {
          map.removeLayer(hitAreaLayerId)
        }
        
        // Remove unpaved layer
        if (map.getLayer(unpavedLayerId)) {
          map.removeLayer(unpavedLayerId)
        }
        if (map.getSource(unpavedSourceId)) {
          map.removeSource(unpavedSourceId)
        }
        
        // Remove main route layer
        if (map.getLayer(staticLayerId)) {
          map.removeLayer(staticLayerId)
        }
        
        // Remove stroke layer
        if (map.getLayer(strokeLayerId)) {
          map.removeLayer(strokeLayerId)
        }
        
        // Remove source (after all layers using it are removed)
        if (map.getSource(staticSourceId)) {
          map.removeSource(staticSourceId)
        }
        
        console.log('🧹 Removed static route and unpaved layers')
        
        // Remove hover and click event listeners
        try {
          const hitAreaLayerId = 'embed-static-routes-hit-area'
          map.off('mouseenter', hitAreaLayerId)
          map.off('mouseleave', hitAreaLayerId)
          map.off('click', hitAreaLayerId)
          console.log('🧹 Removed static route event listeners')
        } catch (error) {
          console.warn('Error removing event listeners:', error)
        }
        
        // Clean up any remaining static hover panel
        try {
          const existingPanel = document.querySelector('[data-static-hover="true"]')
          if (existingPanel) {
            existingPanel.remove()
          }
          // Also clean up global reference
          if ((window as any).__staticHoverPanel) {
            (window as any).__staticHoverPanel = null
          }
        } catch (error) {
          console.warn('Error removing hover panel:', error)
        }
      } catch (error) {
        console.warn('Error removing static route layers:', error)
      }
      
      // Clear route references from unified renderer
      if (unifiedRendererRef.current) {
        unifiedRendererRef.current.clearSelectedRoute()
        unifiedRendererRef.current.clearHoveredRoute()
      }
      
      // Try multiple cleanup approaches for any remaining routes
      const renderer = routeRendererRef.current
      if (renderer) {
        renderer.clearRenderedRoute(true) // Force clear all routes
      }
      
      // Also manually remove any route-related layers that might be lingering
      setTimeout(() => {
        if (map && map.getStyle) {
          try {
            const style = map.getStyle()
            if (style && style.layers) {
            const routeLayerIds = style.layers
              .filter((layer: any) => 
                layer.id.includes('route-') || 
                layer.id.includes('unpaved-') ||
                layer.id.includes('distance-') ||
                layer.id.includes('arrow-')
              )
              .map((layer: any) => layer.id)
            
            console.log('🧹 Found route layers to remove:', routeLayerIds)
            
            routeLayerIds.forEach(layerId => {
              try {
                if (map.getLayer(layerId)) {
                  map.removeLayer(layerId)
                }
                if (map.getSource(layerId)) {
                  map.removeSource(layerId)
                }
              } catch (error) {
                console.warn('Error manually removing layer:', layerId, error)
              }
            })
          }
          } catch (error) {
            console.warn('Error during route cleanup:', error)
          }
          
          // Recreate a fresh RouteRenderer after cleanup
          setTimeout(() => {
            if (!alwaysRenderRoutes && map) {
              console.log('🎨 Creating fresh RouteRenderer after cleanup')
              if (routeRendererRef.current) {
                routeRendererRef.current.destroy()
              }
              routeRendererRef.current = new RouteRenderer(map)
            }
          }, 200)
        }
      }, 100) // Small delay to let renderer cleanup first
    }
    
  }, [alwaysRenderRoutes, showMarkers, map, publishedRoutes, onLazyLoadRoute]) // Include publishedRoutes and onLazyLoadRoute to render when routes load
  
  // Update route colors when they change
  useEffect(() => {
    if (!map || !publishedRoutes?.length) return
    
    if (alwaysRenderRoutes) {
      // Update static route colors
      const staticSourceId = 'embed-static-routes'
      const source = map.getSource(staticSourceId) as mapboxgl.GeoJSONSource
      if (!source) return
      
      console.log('🎨 Updating static route colors')
      
      // Create updated features with new colors
      const allRouteFeatures: any[] = []
      
      publishedRoutes.forEach(route => {
        const routeColor = route.color || '#ef4444'
        
        // Handle single segment routes
        if (route.coordinates?.length > 0) {
          allRouteFeatures.push({
            type: 'Feature',
            properties: {
              routeId: route.id,
              color: routeColor,
              name: route.name
            },
            geometry: {
              type: 'LineString',
              coordinates: route.coordinates
            }
          })
        }
        
        // Handle multi-segment routes
        if (route.routes?.length > 0) {
          route.routes.forEach((segment: any, segmentIndex: number) => {
            if (segment.coordinates?.length > 0) {
              allRouteFeatures.push({
                type: 'Feature',
                properties: {
                  routeId: `${route.id}-segment-${segmentIndex}`,
                  color: routeColor,
                  name: route.name
                },
                geometry: {
                  type: 'LineString',
                  coordinates: segment.coordinates
                }
              })
            }
          })
        }
      })
      
      // Update the source data
      source.setData({
        type: 'FeatureCollection',
        features: allRouteFeatures
      })
    }
    
  }, [publishedRoutes, alwaysRenderRoutes, map]) // Re-run when publishedRoutes change (includes color updates)
  
  // Optimize route rendering based on zoom level when alwaysRenderRoutes is true
  useEffect(() => {
    if (!alwaysRenderRoutes || !map || !publishedRoutes?.length) return
    
    const handleZoomChange = () => {
      const zoom = map.getZoom()
      const renderer = routeRendererRef.current
      
      if (!renderer) return
      
      // At low zoom levels (< 8), hide detailed route features for better performance
      if (zoom < 8) {
        // Hide distance markers and directional arrows on all routes for performance
        publishedRoutes.forEach(route => {
          if (route.routes?.length > 0 || route.coordinates?.length > 0) {
            try {
              // Hide distance markers
              const markersSourceId = `distance-markers-${route.id}`
              const markersLayerId = `distance-markers-layer-${route.id}`
              if (map.getLayer(markersLayerId)) {
                map.setLayoutProperty(markersLayerId, 'visibility', 'none')
              }
              
              // Hide directional arrows  
              const arrowsSourceId = `directional-arrows-${route.id}`
              const arrowsLayerId = `directional-arrows-layer-${route.id}`
              if (map.getLayer(arrowsLayerId)) {
                map.setLayoutProperty(arrowsLayerId, 'visibility', 'none')
              }
            } catch (error) {
              // Ignore errors for non-existent layers
            }
          }
        })
      } else {
        // At higher zoom levels, show all details
        publishedRoutes.forEach(route => {
          if (route.routes?.length > 0 || route.coordinates?.length > 0) {
            try {
              // Show distance markers
              const markersLayerId = `distance-markers-layer-${route.id}`
              if (map.getLayer(markersLayerId)) {
                map.setLayoutProperty(markersLayerId, 'visibility', 'visible')
              }
              
              // Show directional arrows
              const arrowsLayerId = `directional-arrows-layer-${route.id}`
              if (map.getLayer(arrowsLayerId)) {
                map.setLayoutProperty(arrowsLayerId, 'visibility', 'visible')
              }
            } catch (error) {
              // Ignore errors for non-existent layers
            }
          }
        })
      }
    }
    
    // Initial check
    handleZoomChange()
    
    // Listen for zoom changes
    map.on('zoom', handleZoomChange)
    
    return () => {
      map.off('zoom', handleZoomChange)
    }
  }, [alwaysRenderRoutes, map, publishedRoutes])

  // Track which routes have been pre-loaded to avoid duplicates
  const preloadedRoutesRef = useRef<Set<string>>(new Set())
  
  // Pre-load route data when routes are loaded
  useEffect(() => {
    if (!publishedRoutes || publishedRoutes.length === 0 || !onLazyLoadRoute) return
    
    // Pre-load the first few routes immediately
    const routesToPreload = publishedRoutes.slice(0, 6) // Preload first 6 routes
    
    // Filter out already preloaded routes
    const newRoutesToPreload = routesToPreload.filter(route => 
      !preloadedRoutesRef.current.has(route.id)
    )
    
    if (newRoutesToPreload.length === 0) {
      return // All routes already preloaded
    }
    
    console.log('🚀 Pre-loading route data for flicker-free experience')
    
    newRoutesToPreload.forEach(async (route, index) => {
      // Mark as preloading to prevent duplicates
      preloadedRoutesRef.current.add(route.id)
      
      // Stagger the preloading to avoid overwhelming the network
      setTimeout(async () => {
        const coords = route.coordinates?.length || 0
        const routesCoords = route.routes?.[0]?.coordinates?.length || 0
        const hasFullRoute = coords > 1 || routesCoords > 1
        
        if (!hasFullRoute) {
          console.log(`📦 Pre-loading route ${route.id}`)
          try {
            await onLazyLoadRoute(route.id)
            console.log(`✅ Pre-loaded route ${route.id}`)
          } catch (error) {
            console.warn(`❌ Failed to pre-load route ${route.id}:`, error)
            // Remove from preloaded set on error so it can be retried
            preloadedRoutesRef.current.delete(route.id)
          }
        }
      }, index * 100) // 100ms between each preload
    })
  }, [publishedRoutes, onLazyLoadRoute])

  // Add published route markers with clustering
  useEffect(() => {
    console.log('🔄 Marker useEffect running, showMarkers:', showMarkers, 'routes:', publishedRoutes?.length)
    console.log('   Dependencies:', {
      map: !!map,
      publishedRoutes: publishedRoutes?.length,
      onRouteMarkerClick: !!onRouteMarkerClick,
      onRouteSelect: !!onRouteSelect,
      selectedRoute: selectedRoute?.id,
      alwaysRenderRoutes,
      showMarkers,
      onLazyLoadRoute: !!onLazyLoadRoute
    })
    if (!map || !publishedRoutes || publishedRoutes.length === 0) return
    
    // Toggle marker visibility with CSS
    if (!showMarkers) {
      console.log('👻 Hiding all route markers')
      // Add a CSS rule to hide all markers
      const style = document.getElementById('hide-route-markers') || document.createElement('style')
      style.id = 'hide-route-markers'
      style.textContent = '.route-marker { display: none !important; }'
      if (!document.getElementById('hide-route-markers')) {
        document.head.appendChild(style)
      }
      return
    } else {
      // Remove the CSS rule to show markers
      console.log('👁️ Showing all route markers')
      const style = document.getElementById('hide-route-markers')
      if (style) {
        style.remove()
      }
    }
    
    // Check if we should skip marker recreation
    const shouldSkipMarkerRecreation = markersInitializedRef.current && 
      publishedRoutes.length > 0 && 
      markersRef.current.length > 0 &&
      hoveredRouteId !== null // Don't recreate if actively hovering
    
    if (shouldSkipMarkerRecreation) {
      console.log('⏭️ Skipping marker recreation - markers already exist and route is being hovered')
      return
    }
    
    // Give the map a moment to be ready
    const timeoutId = setTimeout(() => {
      if (!map) return
      
      // Additional safety check - ensure map still has required methods
      if (typeof map.getCanvasContainer !== 'function') {
        console.warn('⚠️ Map instance is no longer valid in timeout - skipping marker creation')
        return
      }
      
      console.log('⏰ Inside setTimeout - showMarkers:', showMarkers)
      if (!showMarkers) {
        console.log('❌ showMarkers is false inside timeout - this should not happen!')
        return
      }
      
      markersInitializedRef.current = true

    // Initialize Supercluster for route clustering
    // Disable clustering when routes are rendered permanently or when explicitly disabled
    const routeCluster = new Supercluster({
      radius: 60,
      maxZoom: (disableRouteClustering || alwaysRenderRoutes) ? 0 : 14, // Disable clustering if routes are permanent
      minZoom: 0,
      minPoints: 2
    })

    // Prepare route points for clustering
    const routePoints: GeoJSON.Feature<GeoJSON.Point>[] = []
    publishedRoutes.forEach((route, index) => {
      // Get coordinates - either from first coordinate or center of route
      const coords = getRouteCoordinates(route)
      if (!coords) {
        return // Skip if no coordinates
      }
      const [lng, lat] = coords

      routePoints.push({
        type: 'Feature',
        properties: {
          cluster: false,
          routeId: route.id,
          routeIndex: index,
          routeType: getRouteTypeDisplay(route.routeType || 'single')
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat]
        }
      })
    })

    // Load points into cluster
    routeCluster.load(routePoints)
    clusterRef.current = routeCluster

    // Function to update markers based on current view
    const updateRouteMarkers = () => {
      console.log('🔄 updateRouteMarkers called, showMarkers:', showMarkers)
      if (!map) return
      
      // Don't skip if markers exist - we might just need to hide/show them
      if (!showMarkers && markersRef.current.length === 0) {
        console.log('⏹️ No markers to update - showMarkers is false')
        return
      }
      
      // Ensure route renderer exists
      if (!routeRendererRef.current) {
        console.log('🎨 Creating RouteRenderer in updateRouteMarkers')
        routeRendererRef.current = new RouteRenderer(map)
      }

      // Remove existing route markers (not club markers)
      const existingRouteMarkers = markersRef.current.filter(marker => {
        const element = marker.getElement()
        return element && (element.classList.contains('route-marker') || element.classList.contains('route-cluster-marker'))
      })
      existingRouteMarkers.forEach(marker => {
        marker.remove()
        const index = markersRef.current.indexOf(marker)
        if (index > -1) {
          markersRef.current.splice(index, 1)
        }
      })

      const bounds = map.getBounds()
      if (!bounds) return
      const zoom = Math.floor(map.getZoom())

      const clusters = routeCluster.getClusters([
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth()
      ], zoom)

      // Use the shared route renderer from the component
      const sharedRouteRenderer = routeRendererRef.current
      
      clusters.forEach((cluster: GeoJSON.Feature<GeoJSON.Point>) => {
        const [longitude, latitude] = cluster.geometry.coordinates
        const isCluster = cluster.properties?.cluster
        const pointCount = cluster.properties?.point_count || 0

        if (isCluster) {
          createClusterMarker({
            cluster,
            map,
            clusterRef,
            publishedRoutes,
            unifiedRenderer: unifiedRendererRef.current,
            onRouteSelect,
            selectedRoute,
            markersRef,
            activeHoverRef,
            setHoveredRouteId,
            onLazyLoadRoute
          })
        } else {
          // Create individual route marker
          const routeIndex = cluster.properties?.routeIndex
          if (routeIndex === undefined) return
          const route = publishedRoutes[routeIndex]
          
          console.log('📍 About to create single route marker from clustering, showMarkers:', showMarkers)
          createSingleRouteMarker({
            route,
            map,
            unifiedRenderer: unifiedRendererRef.current,
            onRouteSelect,
            selectedRoute,
            markersRef,
            alwaysRenderRoutes,
            onLazyLoadRoute,
            activeHoverRef,
            activeSelectionRef,
            setHoveredRouteId,
            onPhotoLoad,
            embedMode
          })
        }
      })
    }

    // Initial render
    updateRouteMarkers()

    // Track last zoom level to avoid unnecessary updates
    let lastZoom = Math.floor(map.getZoom())
    
    // Only update markers when zoom level changes significantly
    const handleMapUpdate = () => {
      const currentZoom = Math.floor(map.getZoom())
      
      // Only update if zoom level changed (affects clustering)
      if (currentZoom !== lastZoom) {
        console.log(`🔍 Zoom changed from ${lastZoom} to ${currentZoom}, updating markers`)
        lastZoom = currentZoom
        updateRouteMarkers()
      }
    }
    
    // Only listen for zoom changes (clustering breakpoints)
    map.on('zoomend', handleMapUpdate)
    
    // Also listen for style changes to recreate markers
    const handleStyleLoad = () => {
      console.log('🎨 Map style changed, recreating markers...')
      // Give the new style more time to fully load
      setTimeout(() => {
        try {
          updateRouteMarkers()
          console.log('✅ Successfully recreated markers after style change')
        } catch (error) {
          console.error('❌ Failed to recreate markers after style change:', error)
          // Try again after a longer delay
          setTimeout(() => {
            try {
              updateRouteMarkers()
              console.log('✅ Successfully recreated markers on retry')
            } catch (retryError) {
              console.error('❌ Failed to recreate markers on retry:', retryError)
            }
          }, 500)
        }
      }, 300) // Increased from 100ms to 300ms
    }
    map.on('style.load', handleStyleLoad)

    // Cleanup
    return () => {
      try {
        if (map) {
          map.off('zoomend', handleMapUpdate)
          map.off('style.load', handleStyleLoad)
        }
        markersRef.current.forEach(marker => {
          try {
            marker.remove()
          } catch (error) {
            console.warn('Error removing marker:', error)
          }
        })
        markersRef.current = []
        
        // Don't destroy route renderer here - let the map effect handle it
        // This prevents the renderer from being destroyed while markers are being recreated
      } catch (error) {
        console.warn('Error in RouteMarkers cleanup:', error)
      }
    }
    }, 200) // Small delay to ensure map is ready
    
    return () => {
      console.log('🧹 Cleanup function called - clearing timeout')
      clearTimeout(timeoutId)
      markersInitializedRef.current = false
    }
  }, [map, publishedRoutes, onRouteMarkerClick, onRouteSelect, selectedRoute, alwaysRenderRoutes, showMarkers, onLazyLoadRoute, disableRouteClustering, onPhotoLoad, embedMode])

  // POI rendering for embed mode
  useEffect(() => {
    if (!map || !embedConfig?.display?.showPOIs || !publishedRoutes.length) {
      // Clean up POIs if they should be hidden
      if (poiRendererRef.current) {
        poiRendererRef.current.destroy()
        poiRendererRef.current = null
      }
      return
    }
    
    // Initialize POI renderer if needed
    if (!poiRendererRef.current) {
      // Use scale of 0.5 (half size) for embed mode
      const scale = embedMode ? 0.5 : 1
      poiRendererRef.current = new POIRenderer(map, undefined, onPOISelect, scale)
    }
    
    // Collect all POIs from routes that have POI data
    const allPOIs: any[] = []
    
    publishedRoutes.forEach(route => {
      if (route.pois && route.pois.length > 0) {
        console.log(`📍 Route ${route.id} has ${route.pois.length} POIs`)
        
        // Log POI categories in this route
        const categoriesInRoute = new Set(route.pois.map(poi => poi.category))
        console.log(`📍 Categories in route: ${Array.from(categoriesInRoute).join(', ')}`)
        
        // Filter POIs by visible categories
        const filteredPOIs = route.pois.filter(poi => {
          const isVisible = visiblePOICategories.includes(poi.category)
          if (!isVisible && visiblePOICategories.length > 0) {
            console.log(`🚫 Filtering out POI "${poi.name}" (category: ${poi.category}) - not in visible categories: ${visiblePOICategories.join(', ')}`)
          }
          return isVisible
        })
        
        console.log(`📍 After filtering: ${filteredPOIs.length} POIs remain visible`)
        allPOIs.push(...filteredPOIs)
      }
    })
    
    console.log(`🎯 Rendering ${allPOIs.length} POIs from ${publishedRoutes.length} routes with visible categories: ${visiblePOICategories.join(', ')}`)
    
    // Render the POIs
    poiRendererRef.current.renderPOIs(allPOIs)
    
    return () => {
      if (poiRendererRef.current) {
        poiRendererRef.current.destroy()
        poiRendererRef.current = null
      }
    }
  }, [map, embedConfig, publishedRoutes, visiblePOICategories])

  return null
}
